import { useState } from "react";
import { GALLERY_FRAMES, GallerySize } from "@/data/galleryData";

/** Number of images visible at a time. */
const STEP = 10;
/** Maximum number of frames in the gallery. */
const MAX = GALLERY_FRAMES.length;

// All tiles render as uniform 1:1 squares.
const SPAN: Record<GallerySize, string> = {
  sm: "col-span-1 row-span-1",
  tall: "col-span-1 row-span-1",
  wide: "col-span-1 row-span-1",
  lg: "col-span-1 row-span-1",
};

const PhotoGallery = () => {
  const [visible, setVisible] = useState(STEP);

  const shown = GALLERY_FRAMES.slice(0, visible);
  const hasMore = visible < MAX;

  return (
    <section className="bg-[#FAF6F0] py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10 space-y-3">
          <p className="text-xs font-semibold text-[#9E1B50] uppercase tracking-widest italic">
            Moments & Memories
          </p>
          <h2 className="font-serif text-3xl md:text-5xl font-normal text-[#380C1E]">
            From the road, the room, and the mic.
          </h2>
          <p className="text-[#380C1E]/70 text-sm md:text-base font-light leading-relaxed">
            A look behind the conversations, events, and moments that shape the
            journey from burnout to becoming.
          </p>
        </div>

        {/* Uniform 1:1 square grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {shown.map((frame) => {
            const isVideo = frame.src.endsWith(".mp4");
            const isEmpty = !frame.src;
            return (
              <div
                key={frame.id}
                className={`group relative overflow-hidden rounded-2xl md:rounded-3xl bg-neutral-100 shadow-sm border border-[#E8DEC8] aspect-square ${SPAN[frame.size]}`}
              >
                {isEmpty ? (
                  <div className="w-full h-full flex items-center justify-center bg-[#FBF1F4]">
                    <span className="text-[#380C1E]/30 text-[10px] uppercase tracking-widest font-semibold">
                      Photo
                    </span>
                  </div>
                ) : isVideo ? (
                  <video
                    src={frame.src}
                    className="w-full h-full object-cover block transition-transform duration-500 group-hover:scale-105"
                    muted
                    loop
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  <img
                    src={frame.src}
                    alt={frame.alt}
                    loading="lazy"
                    className="w-full h-full object-cover block transition-transform duration-500 group-hover:scale-105"
                  />
                )}
                {frame.caption && !isEmpty && (
                  <div className="absolute inset-0 bg-gradient-to-t from-[#380C1E]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                    <span className="text-white text-xs font-medium uppercase tracking-wider">
                      {frame.caption}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Show More / Show Less */}
        <div className="mt-8 text-center">
          {hasMore ? (
            <button
              type="button"
              onClick={() => setVisible((v) => Math.min(v + STEP, MAX))}
              className="bg-transparent border border-[#4A0E26] text-[#4A0E26] hover:bg-[#4A0E26] hover:text-white font-bold uppercase text-[11px] tracking-wider px-8 py-3.5 rounded-full transition-all"
            >
              Show More
            </button>
          ) : visible > STEP ? (
            <button
              type="button"
              onClick={() => setVisible(STEP)}
              className="bg-transparent border border-[#4A0E26]/40 text-[#4A0E26]/70 hover:bg-[#4A0E26] hover:text-white font-bold uppercase text-[11px] tracking-wider px-8 py-3.5 rounded-full transition-all"
            >
              Show Less
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default PhotoGallery;
